import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import type { Slide } from './SlidesSidebar';

interface SlideEditorProps {
  slide: Slide;
  onUpdate: (updates: Partial<Slide>) => void;
}

export function SlideEditor({ slide, onUpdate }: SlideEditorProps) {
  const [title, setTitle] = useState(slide.title);
  const [content, setContent] = useState(slide.content);

  // Sync with prop changes
  useEffect(() => {
    setTitle(slide.title);
    setContent(slide.content);
  }, [slide.id, slide.title, slide.content]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value);
      onUpdate({ title: e.target.value });
    },
    [onUpdate]
  );

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setContent(e.target.value);
      onUpdate({ content: e.target.value });
    },
    [onUpdate]
  );

  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div
        className={clsx(
          'w-full max-w-4xl aspect-video',
          'bg-surface-secondary border border-border-primary rounded-xl',
          'shadow-lg overflow-hidden'
        )}
      >
        {slide.template === 'title' && (
          <TitleSlide title={title} onTitleChange={handleTitleChange} />
        )}

        {slide.template === 'content' && (
          <ContentSlide
            title={title}
            content={content}
            onTitleChange={handleTitleChange}
            onContentChange={handleContentChange}
          />
        )}

        {slide.template === 'two-column' && (
          <TwoColumnSlide
            title={title}
            content={content}
            onTitleChange={handleTitleChange}
            onContentChange={handleContentChange}
          />
        )}

        {slide.template === 'image' && (
          <ImageSlide
            title={title}
            content={content}
            onTitleChange={handleTitleChange}
            onContentChange={handleContentChange}
          />
        )}

        {slide.template === 'blank' && (
          <BlankSlide content={content} onContentChange={handleContentChange} />
        )}
      </div>
    </div>
  );
}

// Template components
interface TitleSlideProps {
  title: string;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function TitleSlide({ title, onTitleChange }: TitleSlideProps) {
  return (
    <div className="w-full h-full flex items-center justify-center p-12">
      <input
        type="text"
        value={title}
        onChange={onTitleChange}
        placeholder="Clicca per aggiungere un titolo"
        className={clsx(
          'w-full text-center text-4xl font-bold',
          'bg-transparent border-0 text-text-primary placeholder-text-quaternary',
          'focus:outline-none focus:ring-0'
        )}
      />
    </div>
  );
}

interface ContentSlideProps {
  title: string;
  content: string;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

function ContentSlide({ title, content, onTitleChange, onContentChange }: ContentSlideProps) {
  return (
    <div className="w-full h-full flex flex-col p-12">
      <input
        type="text"
        value={title}
        onChange={onTitleChange}
        placeholder="Titolo slide"
        className={clsx(
          'w-full text-3xl font-bold mb-8',
          'bg-transparent border-0 text-text-primary placeholder-text-quaternary',
          'focus:outline-none focus:ring-0'
        )}
      />
      <textarea
        value={content}
        onChange={onContentChange}
        placeholder="Clicca per aggiungere il contenuto..."
        className={clsx(
          'flex-1 w-full text-lg resize-none',
          'bg-transparent border-0 text-text-secondary placeholder-text-quaternary',
          'focus:outline-none focus:ring-0'
        )}
      />
    </div>
  );
}

interface TwoColumnSlideProps {
  title: string;
  content: string;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

function TwoColumnSlide({ title, content, onTitleChange, onContentChange }: TwoColumnSlideProps) {
  // Split content by newline to separate left and right columns
  const [leftContent, rightContent] = content.split('|||');

  const handleColumnChange = (left: string, right: string) => {
    const combined = `${left}|||${right}`;
    onContentChange({ target: { value: combined } } as React.ChangeEvent<HTMLTextAreaElement>);
  };

  return (
    <div className="w-full h-full flex flex-col p-12">
      <input
        type="text"
        value={title}
        onChange={onTitleChange}
        placeholder="Titolo slide"
        className={clsx(
          'w-full text-3xl font-bold mb-8',
          'bg-transparent border-0 text-text-primary placeholder-text-quaternary',
          'focus:outline-none focus:ring-0'
        )}
      />
      <div className="flex-1 flex gap-8">
        <textarea
          value={leftContent || ''}
          onChange={(e) => handleColumnChange(e.target.value, rightContent || '')}
          placeholder="Colonna sinistra..."
          className={clsx(
            'flex-1 text-base resize-none p-4 rounded-lg',
            'bg-surface-tertiary/50 border border-border-primary',
            'text-text-secondary placeholder-text-quaternary',
            'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
          )}
        />
        <textarea
          value={rightContent || ''}
          onChange={(e) => handleColumnChange(leftContent || '', e.target.value)}
          placeholder="Colonna destra..."
          className={clsx(
            'flex-1 text-base resize-none p-4 rounded-lg',
            'bg-surface-tertiary/50 border border-border-primary',
            'text-text-secondary placeholder-text-quaternary',
            'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
          )}
        />
      </div>
    </div>
  );
}

interface ImageSlideProps {
  title: string;
  content: string;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

function ImageSlide({ title, content, onTitleChange, onContentChange }: ImageSlideProps) {
  return (
    <div className="w-full h-full flex flex-col p-12">
      <input
        type="text"
        value={title}
        onChange={onTitleChange}
        placeholder="Didascalia immagine"
        className={clsx(
          'w-full text-xl font-medium mb-4 text-center',
          'bg-transparent border-0 text-text-primary placeholder-text-quaternary',
          'focus:outline-none focus:ring-0'
        )}
      />
      <div
        className={clsx(
          'flex-1 rounded-lg border-2 border-dashed border-border-primary',
          'flex items-center justify-center',
          'bg-surface-tertiary/30'
        )}
      >
        {content ? (
          <img
            src={content}
            alt={title}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        ) : (
          <div className="text-center">
            <p className="text-text-quaternary mb-2">Trascina un'immagine qui</p>
            <p className="text-xs text-text-quaternary">oppure</p>
            <input
              type="text"
              value={content}
              onChange={(e) =>
                onContentChange({
                  target: { value: e.target.value },
                } as React.ChangeEvent<HTMLTextAreaElement>)
              }
              placeholder="Incolla URL immagine"
              className={clsx(
                'mt-2 px-3 py-1.5 text-sm rounded-md',
                'bg-surface-primary border border-border-primary text-text-secondary',
                'focus:outline-none focus:ring-2 focus:ring-accent-violet'
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface BlankSlideProps {
  content: string;
  onContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

function BlankSlide({ content, onContentChange }: BlankSlideProps) {
  return (
    <div className="w-full h-full p-8">
      <textarea
        value={content}
        onChange={onContentChange}
        placeholder="Contenuto libero..."
        className={clsx(
          'w-full h-full text-lg resize-none',
          'bg-transparent border-0 text-text-secondary placeholder-text-quaternary',
          'focus:outline-none focus:ring-0'
        )}
      />
    </div>
  );
}
